import React, { useContext, useEffect, useState } from 'react';
import { View, TextInput, Text } from 'react-native';
import { Components, Theme } from '@oneamerica/dxp-ui-components';
import { useNavigation } from '@react-navigation/native';
import RouteConstants from '../../../../../constants/RouteConstants';
import BreakpointConstants from '../../../../../constants/BreakpointConstants';
import responsiveStyle from './style';
import { formatCurrency } from '../../../../../utils';
import ColorConstants from '../../../../../constants/ColorConstants';

// Helper function to get responsive styles
const getResponsiveStyles = (width: number, style: any) => {
  if (BreakpointConstants.isLG(width)) {
    return {
      modalStyle: style.lgModalSize,
      checkboxWrapperStyle: style.lgCheckboxWrapper,
      checkboxTextStyle: style.lgCheckboxText,
      currencyWrapperStyle: style.lgCurrencyWrapper,
      headerCheckboxTextStyle: style.lgHeaderCheckboxTextStyle,
      modalTitleStyle: style.lgModalTitle,
      modalSubTitleStyle: style.lgModalSubTitle,
    };
  }
  if (BreakpointConstants.isMD(width)) {
    return {
      modalStyle: style.mdModalSize,
      checkboxWrapperStyle: style.mdCheckboxWrapper,
      checkboxTextStyle: style.mdCheckboxText,
      currencyWrapperStyle: style.mdCurrencyWrapper,
      headerCheckboxTextStyle: style.mdHeaderCheckboxTextStyle,
      modalTitleStyle: style.mdModalTitle,
      modalSubTitleStyle: style.mdModalSubTitle,
    };
  }
  return {
    modalStyle: style.smModalSize,
    checkboxWrapperStyle: style.smCheckboxWrapper,
    checkboxTextStyle: style.smCheckboxText,
    currencyWrapperStyle: style.smCurrencyWrapper,
    headerCheckboxTextStyle: style.smHeaderCheckboxTextStyle,
    modalTitleStyle: style.smModalTitle,
    modalSubTitleStyle: style.smModalSubTitle,
  };
};

const MakePaymentModal = ({
  isModalVisible,
  setModalVisible,
  payments = [],
  onCloseAnalytics,
  onSubmit = () => {},
  allowLoanPayment = false,
  allowLoanInterest = false,
  loanPayoffAmount = 0,
  allowPremiumPayment = false,
  hasPendingPremiumPayment = false,
  hasPendingLoanPayment = false,

  loanBalance = 0,
}: {
  isModalVisible: boolean;
  setModalVisible: CallableFunction;
  payments: { title: string; amount: number }[];
  onCloseAnalytics?: CallableFunction;
  onSubmit?: (selectedPayments: { title: string; amount: number }[]) => void;
  allowLoanPayment?: boolean;
  allowLoanInterest?: boolean;
  loanPayoffAmount?: number;
  allowPremiumPayment?: boolean;
  hasPendingPremiumPayment?: boolean;
  hasPendingLoanPayment?: boolean;
  loanBalance?: number;
}) => {
  const [checked, setChecked] = useState<
    { index: number; isChecked: boolean }[]
  >([]);
  const [loanPaymentValue, setLoanPaymentValue] = useState('');
  const [loanInterestValue, setLoanInterestValue] = useState('');
  const [loanPaymentChecked, setLoanPaymentChecked] = useState(false);
  const [loanInterestChecked, setLoanInterestChecked] = useState(false);
  const [loanPayoffChecked, setLoanPayoffChecked] = useState(false);
  const [loanPaymentError, setLoanPaymentError] = useState('');
  const [loanInterestError, setLoanInterestError] = useState('');

  // Disable Loan Payment and Loan Payoff when the policy is not paid-to-date,

  const disableLoanPaymentOptions =
    allowLoanPayment === false && loanBalance > 0;

  // display a message that the policy is not paid-to-date
  const showPolicyNotCurrentMsg =
    allowPremiumPayment === true &&
    allowLoanPayment === false &&
    allowLoanInterest === false &&
    loanBalance > 0;

  const shouldShowLoanPaymentSection = () => {
    const hasLoanOptions =
      (allowLoanPayment || allowLoanInterest) &&
      hasPendingLoanPayment === false;
    return hasLoanOptions || showPolicyNotCurrentMsg;
  };

  const isPaidUpAdditionSelected = () => {
    const paidUpIndex = payments.findIndex(
      (p) => p.title === 'Paid Up Addition',
    );
    return checked.some((c) => c.index === paidUpIndex);
  };

  const navigation = useNavigation<any>();
  const style = responsiveStyle();
  const { width } = useContext(Theme.ResponsiveContext);

  const {
    modalStyle,
    checkboxWrapperStyle,
    checkboxTextStyle,
    currencyWrapperStyle,
    headerCheckboxTextStyle,
    modalTitleStyle,
    modalSubTitleStyle,
  } = getResponsiveStyles(width, style);

  useEffect(() => {
    if (isModalVisible) {
      resetModalState();
    }
  }, [isModalVisible]);

  const resetModalState = () => {
    setLoanPaymentValue('');
    setLoanInterestValue('');
    setLoanPaymentChecked(false);
    setLoanInterestChecked(false);
    setLoanPayoffChecked(false);
    setLoanPaymentError('');
    setLoanInterestError('');
  };

  const toggleLoanPayment = (enable: boolean) => {
    setLoanPaymentChecked(enable);
    if (enable) {
      const error = validateLoanPayment(loanPaymentValue, true);
      setLoanPaymentError(error);
    } else {
      setLoanPaymentValue('');
      setLoanPaymentError('');
    }
  };

  const toggleLoanPayoff = (enable: boolean) => {
    setLoanPayoffChecked(enable);
  };

  const toggleLoanInterest = (enable: boolean) => {
    setLoanInterestChecked(enable);
    if (enable) {
      const error = validateLoanInterest(loanInterestValue, true);
      setLoanInterestError(error);
    } else {
      setLoanInterestValue('');
      setLoanInterestError('');
    }
  };

  const shouldSelectBasePremium = (paymentTitle: string, newChecked: any[]) => {
    if (paymentTitle !== 'Paid Up Addition') return false;
    const basePremiumIndex = payments.findIndex(
      (p) => p.title === 'Base Premium',
    );
    return (
      basePremiumIndex !== -1 &&
      !newChecked.some((c) => c.index === basePremiumIndex)
    );
  };

  const shouldDeselectPaidUpAddition = (paymentTitle: string) => {
    return paymentTitle === 'Base Premium';
  };

  const handlePaymentChange = (index: number, isChecked: boolean) => {
    const paymentTitle = payments[index]?.title;

    setChecked((prev) => {
      let newChecked = [...prev];

      if (isChecked) {
        if (!newChecked.some((c) => c.index === index)) {
          newChecked.push({ index, isChecked: true });
        }

        if (shouldSelectBasePremium(paymentTitle, newChecked)) {
          const basePremiumIndex = payments.findIndex(
            (p) => p.title === 'Base Premium',
          );
          newChecked.push({ index: basePremiumIndex, isChecked: true });
        }
      } else {
        newChecked = newChecked.filter((c) => c.index !== index);

        if (shouldDeselectPaidUpAddition(paymentTitle)) {
          const paidUpAdditionIndex = payments.findIndex(
            (p) => p.title === 'Paid Up Addition',
          );
          if (paidUpAdditionIndex !== -1) {
            newChecked = newChecked.filter(
              (c) => c.index !== paidUpAdditionIndex,
            );
          }
        }
      }

      return newChecked;
    });
  };

  const onClose = () => {
    onCloseAnalytics?.();
    setChecked([]);
    resetModalState();
    setModalVisible(false);
  };

  const validateLoanPayment = (value: string, isChecked: boolean): string => {
    if (isChecked && (!value || value.trim() === '')) {
      return 'Please enter an amount';
    }
    if (
      value &&
      Number.parseFloat(value) > Number.parseFloat(String(loanPayoffAmount))
    ) {
      return 'Amount cannot exceed the loan payoff';
    }
    return '';
  };

  const validateLoanInterest = (value: string, isChecked: boolean): string => {
    if (isChecked && (!value || value.trim() === '')) {
      return 'Please enter the amount from your loan interest bill';
    }
    if (
      value &&
      Number.parseFloat(value) > Number.parseFloat(String(loanPayoffAmount))
    ) {
      return 'Amount cannot exceed the loan payoff';
    }
    return '';
  };

  const handleLoanPaymentChange = (value: string) => {
    // Allow only numeric input with up to 2 decimal places
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value) || value === '') {
      setLoanPaymentValue(value);
      const error = validateLoanPayment(value, loanPaymentChecked);
      setLoanPaymentError(error);
    }
  };

  const handleLoanInterestChange = (value: string) => {
    // Allow only numeric input with up to 2 decimal places
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value) || value === '') {
      setLoanInterestValue(value);
      const error = validateLoanInterest(value, loanInterestChecked);
      setLoanInterestError(error);
    }
  };

  const handleSubmit = () => {
    const selectedPayments = checked
      .map((item) => payments[item.index])
      .filter(Boolean);

    const loanPayments: { title: string; amount: number }[] = [];
    if (loanPaymentChecked && loanPaymentValue) {
      loanPayments.push({
        title: 'Loan payment',
        amount: Number.parseFloat(loanPaymentValue),
      });
    }
    if (loanInterestChecked && loanInterestValue) {
      loanPayments.push({
        title: 'Loan interest',
        amount: Number.parseFloat(loanInterestValue),
      });
    }
    if (loanPayoffChecked) {
      loanPayments.push({
        title: 'Loan payoff',
        amount: loanPayoffAmount,
      });
    }

    onSubmit([...selectedPayments, ...loanPayments]);
    setChecked([]);
    resetModalState();
    setModalVisible(false);
  };

  const isLoanPayoffConflict =
    loanPayoffChecked && (loanPaymentChecked || loanInterestChecked);

  const hasValidSelections = () => {
    const hasRegularPayments = allowPremiumPayment && checked.length > 0;
    const hasValidLoanPayment =
      allowLoanPayment &&
      loanPaymentChecked &&
      loanPaymentValue &&
      !loanPaymentError;
    const hasValidLoanInterest =
      loanInterestChecked && loanInterestValue && !loanInterestError;
    const hasLoanPayoff = allowLoanPayment && loanPayoffChecked;

    return (
      hasRegularPayments ||
      hasValidLoanPayment ||
      hasValidLoanInterest ||
      hasLoanPayoff
    );
  };

  const hasValidationErrors = () => {
    return loanPaymentError !== '' || loanInterestError !== '';
  };

  const isSubmitDisabled = () => {
    return (
      !hasValidSelections() || hasValidationErrors() || isLoanPayoffConflict
    );
  };

  const getInputEditable = () => {
    return loanPaymentChecked && !disableLoanPaymentOptions;
  };

  const getInputStyle = () => {
    const isDisabled = !loanPaymentChecked || disableLoanPaymentOptions;
    return [
      style.textInput,
      isDisabled && style.textInputDisabled,
      loanPaymentError ? style.textInputError : null,
    ];
  };

  const getLoanInterestInputStyle = () => {
    return [
      style.textInput,
      !loanInterestChecked && style.textInputDisabled,
      loanInterestError ? style.textInputError : null,
    ];
  };

  const phnLinkStyle = {
    style: style.phnlinkStyle,
    linkStyle: style.phnlinkStyle,
    linkHoverStyle: style.phnlinkStyle,
    hoverStyle: style.phnlinkStyle,
    focusStyle: style.phnlinkStyle,
  };

  return (
    <Components.DialogModal
      style={modalStyle}
      width={'auto'}
      padding={20}
      Header={
        <Components.Text testID="modalTitle" style={modalTitleStyle}>
          Please select which payment you'd like to make
          <Components.Text style={modalSubTitleStyle}>
            (Select all that apply)
          </Components.Text>
        </Components.Text>
      }
      visible={isModalVisible}
      onClose={onClose}
      Footer={
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <View style={{ flex: 1 }}>
            {isLoanPayoffConflict && (
              <Text style={style.loanPayoffConflictMessage}>
                {
                  'Loan Payoff cannot be submitted in combination with other Loan payment options'
                }
              </Text>
            )}
            <Text style={style.paymentCutoffMessage}>
              {
                'Payments confirmed by 12:00 PM EST will be posted same day. Payments confirmed after 12:00 PM EST will be posted the following business day.'
              }
            </Text>
          </View>
          <Components.Button
            disabled={isSubmitDisabled()}
            style={[
              !BreakpointConstants.isLG(width) &&
                style.customeServiceRequestBtn,
              style.serviceRequestBtn,
            ]}
            accessibilityRole="button"
            id="btnOutline"
            theme="primary"
            onPress={handleSubmit}
            testID={'submitButton'}
          >
            Submit
          </Components.Button>
        </View>
      }
    >
      {allowPremiumPayment && hasPendingPremiumPayment === false && (
        <View style={style.placeholderWrapper}>
          <View style={style.headerCheckboxContainerStyle}>
            <Components.Text style={headerCheckboxTextStyle}>
              {'Premium payment'}
            </Components.Text>
          </View>
          {payments.map((item, index) => (
            <React.Fragment key={item.title}>
              <View
                className="my-3 flex flex-row items-center"
                style={
                  BreakpointConstants.isSM(width)
                    ? { paddingLeft: 40 }
                    : { paddingLeft: 10 }
                }
              >
                <View style={checkboxWrapperStyle}>
                  <Components.Checkbox
                    key={`${item.title}-${checked.some((c) => c.index === index)}`}
                    testID={item.title}
                    value={!!checked.some((c) => c.index === index)}
                    onChange={(isChecked) =>
                      handlePaymentChange(index, isChecked)
                    }
                  >
                    <Components.Text style={checkboxTextStyle}>
                      {item.title}
                    </Components.Text>
                  </Components.Checkbox>
                </View>
                <Components.Text style={currencyWrapperStyle}>
                  {formatCurrency(item.amount)}
                </Components.Text>
              </View>

              {item.title === 'Paid Up Addition' &&
                isPaidUpAdditionSelected() && (
                  <Components.Text style={style.noteText}>
                    {
                      'Note: Base Premium payment is required to submit PUA Payment '
                    }
                  </Components.Text>
                )}
              {item.title === 'Paid Up Addition' && (
                <View
                  className="flex flex-row items-center justify-start flex-wrap"
                  style={
                    BreakpointConstants.isSM(width)
                      ? { paddingLeft: 40, marginBottom: 12 }
                      : { paddingLeft: 10, marginBottom: 12 }
                  }
                >
                  <Components.Text
                    className="mr-2 text-14"
                    style={{ fontSize: 12, color: ColorConstants.black }}
                  >
                    To make an additional or unscheduled PUA Payment, please
                    submit a
                  </Components.Text>
                  <Components.Link
                    onPress={() => {
                      onClose();
                      navigation.navigate(RouteConstants.policyservicerequest);
                    }}
                    accessibilityLabel="Policy Service Request"
                    accessibilityRole="button"
                    {...phnLinkStyle}
                  >
                    Policy Service Request
                  </Components.Link>
                  <Components.Text
                    style={{ fontSize: 12, color: ColorConstants.black }}
                  >
                    {'.'}
                  </Components.Text>
                </View>
              )}
              <View style={style.horizontalDivider} />
            </React.Fragment>
          ))}
        </View>
      )}
      {shouldShowLoanPaymentSection() && (
        <View>
          {/* Loan Payment Header - Start */}
          <View style={style.headerCheckboxContainerStyle}>
            <Components.Text style={headerCheckboxTextStyle}>
              {'Loan payment'}
            </Components.Text>
          </View>
          {showPolicyNotCurrentMsg ? (
            <Components.Text style={style.loanPaymentDisabledMessage}>
              {
                'Your policy is not currently paid-to-date. Please bring your policy current before making a loan payment.'
              }
            </Components.Text>
          ) : null}
          {/* Loan Payment Header - End */}

          {/* Loan Payment Checkbox - Start */}
          <View
            style={[
              style.loanPaymentRow,
              !BreakpointConstants.isSM(width) && {
                paddingLeft: 10,
                flexDirection: 'column',
                alignItems: 'stretch',
              },
            ]}
          >
            <View style={checkboxWrapperStyle}>
              <Components.Checkbox
                key={`loan-payment-${loanPaymentChecked}`}
                testID={'loanPayment'}
                value={loanPaymentChecked}
                disabled={disableLoanPaymentOptions}
                onChange={toggleLoanPayment}
              >
                <Components.Text style={checkboxTextStyle}>
                  {'Loan payment'}
                </Components.Text>
              </Components.Checkbox>
            </View>
            <View
              style={[
                style.textInputWrapper,
                !BreakpointConstants.isSM(width) && { paddingLeft: 32 },
              ]}
            >
              <View style={style.textInputContainer}>
                <Text style={style.dollarSign}>$</Text>
                <TextInput
                  style={getInputStyle()}
                  value={loanPaymentValue}
                  onChangeText={handleLoanPaymentChange}
                  placeholder="0000.00"
                  placeholderTextColor={ColorConstants.placeholderText}
                  keyboardType="decimal-pad"
                  testID="loanPaymentInput"
                  editable={getInputEditable()}
                />
              </View>
              {loanPaymentError ? (
                <Text
                  style={[
                    style.errorText,
                    !BreakpointConstants.isSM(width) && { marginTop: 20 },
                  ]}
                >
                  {loanPaymentError}
                </Text>
              ) : null}
            </View>
          </View>
          {/* Loan Payment Checkbox - End */}

          {/* Loan Interest Checkbox - Start */}
          {allowLoanInterest && (
            <View
              style={[
                style.loanInterestRowNoBorder,
                !BreakpointConstants.isSM(width) && {
                  paddingLeft: 10,
                  flexDirection: 'column',
                  alignItems: 'stretch',
                },
              ]}
            >
              <View style={checkboxWrapperStyle}>
                <Components.Checkbox
                  key={`loan-interest-${loanInterestChecked}`}
                  testID={'loanInterest'}
                  value={loanInterestChecked}
                  disabled={false}
                  onChange={toggleLoanInterest}
                >
                  <Components.Text style={checkboxTextStyle}>
                    {'Pay my loan interest bill'}
                  </Components.Text>
                </Components.Checkbox>
              </View>
              <View
                style={[
                  style.textInputWrapper,
                  !BreakpointConstants.isSM(width) && { paddingLeft: 32 },
                ]}
              >
                <View style={style.textInputContainer}>
                  <Text style={style.dollarSign}>$</Text>
                  <TextInput
                    style={getLoanInterestInputStyle()}
                    value={loanInterestValue}
                    onChangeText={handleLoanInterestChange}
                    placeholder="0000.00"
                    placeholderTextColor={ColorConstants.placeholderText}
                    keyboardType="decimal-pad"
                    testID="loanInterestInput"
                    editable={loanInterestChecked}
                  />
                </View>
                {loanInterestError ? (
                  <Text
                    style={[
                      style.errorText,
                      !BreakpointConstants.isSM(width) && { marginTop: 16 },
                    ]}
                  >
                    {loanInterestError}
                  </Text>
                ) : null}
              </View>
            </View>
          )}
          {allowLoanInterest && (
            <Components.Text style={style.noteText}>
              {
                'Note: Loan Interest Payment must match the amount billed and paid on the due date, otherwise the payment will be applied to the loan balance'
              }
            </Components.Text>
          )}
          {/* Loan Interest Checkbox - End */}

          {/* Loan Payoff Checkbox - Start */}
          <View
            style={[
              style.loanPayoffRowNoBorder,
              !BreakpointConstants.isSM(width) && { paddingLeft: 10 },
            ]}
          >
            <View style={checkboxWrapperStyle}>
              <Components.Checkbox
                key={`loan-payoff-${loanPayoffChecked}`}
                testID={'LoanPayoff'}
                value={loanPayoffChecked}
                disabled={disableLoanPaymentOptions}
                onChange={toggleLoanPayoff}
              >
                <Components.Text style={checkboxTextStyle}>
                  {'Loan payoff'}
                </Components.Text>
              </Components.Checkbox>
            </View>
            <Components.Text
              style={[
                currencyWrapperStyle,
                disableLoanPaymentOptions && style.disabledCurrencyText,
              ]}
            >
              {formatCurrency(loanPayoffAmount)}
            </Components.Text>
          </View>
          <Components.Text style={style.noteText}>
            {'Note: Payoff amounts are updated daily'}
          </Components.Text>
          {/* Loan Payoff Checkbox - End */}
        </View>
      )}
    </Components.DialogModal>
  );
};

export default MakePaymentModal;
